import React, { useContext, Fragment, useEffect } from "react";
import Checkbox from "../../components/data_entry/checkbox";
import {
  SurveyParticipantDataDocument,
  FlagCategoriesDocument,
  FlagCategory,
  ProgramSegmentDocument,
} from "../../generated/graphql";
import { SurveyContext, QuestionProps } from "../survey";
import TextArea from "../../components/data_entry/text_area";
import { RadioGroup, RadioButton } from "../../components/data_entry/radio";
import QuestionLabel from "./components/question_label";
import styled from "styled-components";
import { gql } from "urql";
import { useQuery } from "urql";

gql`
  query FlagCategories($filter: FlagCategoryFilter) {
    flagCategories(filter: $filter) {
      id
      name
      identifier
      label
    }
  }
`;

export interface Answer {
  selectedFlags: FlagCategory["identifier"][];
  writeIn?: string;
  extraInfo: string;
  shouldSchoolFollowUp: "YES" | "NO" | "NOT_SURE";
}

export interface Customizations {
  prompt: string;
  followUpPrompt: string;
  schoolSupportPrompt: string;
  schoolSupportDescription: string;
  hideSchoolSupportQuestion: boolean;
  description?: string;
}

const FlagsExtraQuestion = styled.div`
  margin-top: ${({ theme }) => theme.deprecatedSpacing.component.eight};
`;

export default function Flags({
  customizations,
  currentAnswer,
  updateAnswer,
  setAbleToSubmit,
}: QuestionProps<Answer, Customizations>) {
  const query = new URLSearchParams(window.location.search);
  const initialCategory = query.get("initialCategory") || "";

  const { participantId, programId = "" } = useContext(SurveyContext);

  const [{ fetching, error, data }] = useQuery({
    query: SurveyParticipantDataDocument,
    variables: { participantId },
    pause: !participantId,
  });

  const [
    { fetching: fetchingProgramSegment, error: errorProgramSegment, data: programSegmentData },
  ] = useQuery({
    query: ProgramSegmentDocument,
    variables: { programId: programId },
    pause: !programId,
  });

  const [{ fetching: categoriesFetching, error: categoriesError, data: categoriesData }] = useQuery(
    {
      query: FlagCategoriesDocument,
      // The flag categories are based on the program, and the program could be obtained
      // from either the current participant or the program id provided from the secret survey preview
      variables: { filter: { programId: data?.participant.program.id || programId || "" } },
      pause: !data?.participant.program.id && !programId,
    },
  );
  // @TODO - we could get more nuanced here if we wanted to the follow-up to be required if something is selected
  const answerIsValid = typeof currentAnswer !== "undefined";
  useEffect(() => {
    setAbleToSubmit(answerIsValid);
  }, [answerIsValid, setAbleToSubmit]);

  // Pre-populate a flag if we have a query string on the url field.
  useEffect(() => {
    const category = categoriesData?.flagCategories.find(
      (flagCategory) => flagCategory.name === initialCategory,
    );
    if (category) {
      updateAnswer({
        selectedFlags: [category.identifier],
        extraInfo: "",
        shouldSchoolFollowUp: "YES",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCategory, categoriesData]);

  if (fetching || data === null || fetchingProgramSegment || programSegmentData === null)
    return <div>Loading...</div>;

  if ((error || !data) && (errorProgramSegment || !programSegmentData)) return <div>Uh oh</div>;

  const program = data?.participant.program || programSegmentData?.program;
  const label = customizations.prompt;
  const followUpLabel = customizations.followUpPrompt;
  const description = customizations.description;
  const schoolSupportLabel = customizations.schoolSupportPrompt;
  const schoolSupportDescriptionLabel = customizations.schoolSupportDescription;
  const hideSchoolSupportQuestion = customizations.hideSchoolSupportQuestion;

  if (categoriesFetching || categoriesData === null) return <div>Loading...</div>;
  if (!categoriesData || categoriesError) return <div>Uh oh</div>;

  const sortedCategories = categoriesData.flagCategories.sort((a, b) =>
    a.label.localeCompare(b.label),
  );

  const answer: Answer = currentAnswer
    ? currentAnswer
    : {
        selectedFlags: [],
        extraInfo: "",
        shouldSchoolFollowUp: "YES",
      };
  return (
    <div className="multi-select">
      <QuestionLabel label={label} description={description} />
      <Checkbox
        label="None"
        checked={!!(currentAnswer && answer.selectedFlags.length === 0)}
        onChange={() => {
          updateAnswer({
            selectedFlags: [],
            extraInfo: "",
            shouldSchoolFollowUp: "YES",
          });
        }}
      />
      {sortedCategories.map((flagCategory) => (
        <Checkbox
          label={flagCategory.label || undefined}
          key={flagCategory.identifier}
          checked={answer.selectedFlags.includes(flagCategory.identifier)}
          onChange={() => {
            const index = answer.selectedFlags.indexOf(flagCategory.identifier);
            const updatedOptions =
              index > -1
                ? [
                    ...answer.selectedFlags.slice(0, index),
                    ...answer.selectedFlags.slice(index + 1),
                  ]
                : [...answer.selectedFlags, flagCategory.identifier];
            updateAnswer(
              currentAnswer
                ? {
                    ...currentAnswer,
                    selectedFlags: updatedOptions,
                  }
                : {
                    selectedFlags: updatedOptions,
                    extraInfo: "",
                    shouldSchoolFollowUp: "YES",
                  },
            );
          }}
        />
      ))}
      {answer.selectedFlags.length > 0 && (
        <Fragment>
          <FlagsExtraQuestion>
            <QuestionLabel label={followUpLabel} />
            <TextArea
              rows={3}
              onChange={({ currentTarget: { value: updatedResponse } }) =>
                updateAnswer(
                  currentAnswer
                    ? {
                        ...currentAnswer,
                        extraInfo: updatedResponse,
                      }
                    : {
                        selectedFlags: [],
                        extraInfo: updatedResponse,
                        shouldSchoolFollowUp: "YES",
                      },
                )
              }
              value={currentAnswer ? currentAnswer.extraInfo : ""}
            />
          </FlagsExtraQuestion>
        </Fragment>
      )}
      {answer.selectedFlags.length > 0 && !hideSchoolSupportQuestion && (
        <Fragment>
          <FlagsExtraQuestion>
            <QuestionLabel label={schoolSupportLabel} description={schoolSupportDescriptionLabel} />
            <RadioGroup
              name="flagSchoolFollowUp"
              value={currentAnswer ? currentAnswer.shouldSchoolFollowUp : "YES"}
              onChange={(newValue) =>
                updateAnswer(
                  currentAnswer
                    ? { ...currentAnswer, shouldSchoolFollowUp: newValue }
                    : {
                        selectedFlags: [],
                        extraInfo: "",
                        shouldSchoolFollowUp: newValue,
                      },
                )
              }
            >
              <>
                <RadioButton
                  label={`Yes — send this information to a ${program?.programSequence.school.shortName} representative`}
                  value="YES"
                />
                <RadioButton label="No immediate support needed" value="NO" />
              </>
            </RadioGroup>
          </FlagsExtraQuestion>
        </Fragment>
      )}
    </div>
  );
}
